// ─── WebhookSettings.jsx ────────────────────────────────────

import { apiFetch } from '../../utils/apiClient';
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef } from 'react';

const mono  = { fontFamily: 'JetBrains Mono, monospace' };
const panel = { background: '#141720', border: '1px solid #ffffff14', borderRadius: 8, padding: '14px 16px' };

const PRESETS = [
  { name: 'Splunk HEC',   url: 'http://splunk:8088/services/collector/event',    note: 'Splunk HTTP Event Collector' },
  { name: 'Elastic',      url: 'http://elastic:9200/_ingest/pipeline/soc',       note: 'Elasticsearch ingest pipeline' },
  { name: 'Generic POST', url: 'https://your-siem.example.com/api/alert',        note: 'Any HTTP endpoint' },
  { name: 'Slack',        url: 'https://hooks.slack.com/services/T.../B.../...', note: 'Slack incoming webhook' },
];

const DELIVERY_LOG_KEY = 'aegis_webhook_log';
const CONFIG_KEY       = 'aegis_webhook_config';
const PROXY_URL        = '/api/webhook-proxy';

// FIX-4: Browser timeout is 60s — backend httpx timeout is 10s.
// Backend will ALWAYS reply before this fires, eliminating the race condition.
const TIMEOUT_MS = 60000;

const TEST_PAYLOAD = {
  source:      'AEGIS SOC',
  version:     '2.1',
  type:        'APT_ALERT',
  severity:    'CRITICAL',
  src_ip:      '10.0.1.49',
  attack_type: 'APT-C2-Communication',
  confidence:  0.98,
  incident_id: 'APT-INC-0001',
  message:     'Webhook test from AEGIS SOC v2.1',
};

function loadConfig() {
  try { return JSON.parse(localStorage.getItem(CONFIG_KEY) || '{}'); } catch { return {}; }
}
function saveConfig(cfg) {
  try { localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg)); } catch {}
}
function loadLog() {
  try { return JSON.parse(localStorage.getItem(DELIVERY_LOG_KEY) || '[]'); } catch { return []; }
}
function saveLog(log) {
  try { localStorage.setItem(DELIVERY_LOG_KEY, JSON.stringify(log)); } catch {}
}

export default function WebhookSettings() {
  const [config, setConfig] = useState(() => ({
    url:      '',
    enabled:  false,
    severity: 'CRITICAL',
    ...loadConfig(),
  }));
  const [deliveryLog, setDeliveryLog] = useState(loadLog);
  const [testStatus,  setTestStatus]  = useState('idle');

  // FIX-2: ref always mirrors latest config — kills stale closure bug
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
    saveConfig(config);
  }, [config]);

  // ── Core delivery — FIX-5: returns true on success, false on any failure ──
  async function deliverWebhook(targetUrl, payload) {
    const entryId = Date.now();
    const entry = {
      id:     entryId,
      ts:     new Date().toISOString(),
      url:    targetUrl,
      status: '...',
      payload,
    };

    setDeliveryLog(prev => {
      const next = [entry, ...prev].slice(0, 20);
      saveLog(next);
      return next;
    });

    const controller = new AbortController();
    // FIX-4: 60s browser timeout — backend always responds in ≤10s
    const timeoutId  = setTimeout(() => controller.abort(), TIMEOUT_MS);
    let success = false;

    try {
      const res = await apiFetch(PROXY_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ url: targetUrl, data: payload }),
        signal:  controller.signal,
      });

      let result;
      try {
        result = await res.json();
      } catch {
        result = {
          status:      res.ok ? 'ok' : 'error',
          http_status: res.status,
          message:     `Non-JSON response (HTTP ${res.status})`,
        };
      }

      if (res.ok && result.status === 'ok') {
        entry.status = `${result.http_status || res.status} OK`;
        success = true;
      } else {
        entry.status = `FAILED: ${result.message || `HTTP ${res.status}`}`;
      }

    } catch (e) {
      if (e.name === 'AbortError') {
        entry.status = `FAILED: Request timeout (>${TIMEOUT_MS / 1000}s)`;
      } else if (e instanceof TypeError) {
        entry.status = `FAILED: Cannot reach proxy — is backend running on :8000?`;
      } else {
        entry.status = `FAILED: ${e.message}`;
      }
    } finally {
      clearTimeout(timeoutId);
      setDeliveryLog(prev => {
        const updated = prev.map(item => item.id === entryId ? { ...entry } : item);
        saveLog(updated);
        return updated;
      });
    }

    return success; // FIX-5
  }

  // ── sendWebhook: auto-alerts path — respects enabled + severity ───────────
  async function sendWebhook(payload) {
    const { enabled, url, severity } = configRef.current; // FIX-2
    if (!enabled || !url) return;

    const severityOrder = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1, ALL: 0 };
    const alertSev  = severityOrder[payload.severity] ?? 0;
    const configSev = severityOrder[severity]         ?? 0;
    if (severity !== 'ALL' && alertSev < configSev) return;

    await deliverWebhook(url, payload);
  }

  // ── FIX-1 + FIX-3: manual test — no guards, real status on button ─────────
  async function testWebhook() {
    const url = configRef.current.url; // FIX-2: never stale
    if (!url) {
      alert('Please enter a Webhook URL first.');
      return;
    }

    setTestStatus('sending');

    // FIX-3: read real success/fail from return value
    const success = await deliverWebhook(url, {
      ...TEST_PAYLOAD,
      timestamp: new Date().toISOString(),
    });

    // FIX-3: button now shows correct result
    setTestStatus(success ? 'sent' : 'error');
    setTimeout(() => setTestStatus('idle'), 3000);
  }

  const inputStyle = {
    background: '#0d1018', border: '1px solid #ffffff22', borderRadius: 5,
    color: '#e8eaf0', padding: '7px 10px', fontSize: 12, ...mono, outline: 'none',
    width: '100%', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 10, color: '#6b7280', ...mono, marginBottom: 5 };

  const btnLabel = {
    idle:    '▶ Send Test Payload',
    sending: `⟳ Sending…`,
    sent:    '✓ Delivered!',
    error:   '✗ Failed — check log',
  }[testStatus];

  const btnColor = testStatus === 'error' ? '#ff3b4e'
                 : testStatus === 'sent'  ? '#10b981'
                 : '#60a5fa';
  const btnBg    = testStatus === 'error' ? '#ff3b4e18'
                 : testStatus === 'sent'  ? '#10b98118'
                 : '#3b82f618';

  return (
    <div style={{ display: 'grid', gap: 14 }}>

      {/* Header */}
      <div style={panel}>
        <div style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em', marginBottom: 4 }}>
          SIEM / WEBHOOK INTEGRATION
        </div>
        <div style={{ fontSize: 11, color: '#4b5563', ...mono }}>
          Send alert payloads to external SIEM, Splunk, Elastic, or any HTTP endpoint
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

        {/* ── Config panel ── */}
        <div style={panel}>
          <div style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em', marginBottom: 12 }}>
            CONFIGURATION
          </div>

          {/* Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, padding: '8px 10px', background: '#0d1018', borderRadius: 6 }}>
            <div>
              <div style={{ fontSize: 12, color: '#e8eaf0', ...mono }}>Webhook enabled</div>
              <div style={{ fontSize: 10, color: '#4b5563', ...mono }}>Fire POST on matching alerts</div>
            </div>
            <div
              onClick={() => setConfig(c => ({ ...c, enabled: !c.enabled }))}
              style={{ width: 40, height: 22, background: config.enabled ? '#10b981' : '#374151', borderRadius: 11, position: 'relative', cursor: 'pointer', transition: 'background .2s' }}
            >
              <div style={{ width: 18, height: 18, background: '#fff', borderRadius: '50%', position: 'absolute', top: 2, left: config.enabled ? 20 : 2, transition: 'left .2s' }} />
            </div>
          </div>

          {/* URL */}
          <div style={{ marginBottom: 10 }}>
            <div style={labelStyle}>WEBHOOK URL</div>
            <input
              style={inputStyle}
              value={config.url}
              placeholder="https://webhook.site/your-id"
              onChange={e => setConfig(c => ({ ...c, url: e.target.value }))}
            />
          </div>

          {/* Severity */}
          <div style={{ marginBottom: 14 }}>
            <div style={labelStyle}>TRIGGER ON SEVERITY</div>
            <select
              style={{ ...inputStyle, width: 'auto' }}
              value={config.severity}
              onChange={e => setConfig(c => ({ ...c, severity: e.target.value }))}
            >
              <option value="CRITICAL">CRITICAL only</option>
              <option value="HIGH">HIGH and above</option>
              <option value="MEDIUM">MEDIUM and above</option>
              <option value="ALL">All alerts</option>
            </select>
          </div>

          {/* Presets */}
          <div style={{ marginBottom: 14 }}>
            <div style={labelStyle}>QUICK PRESETS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {PRESETS.map(p => (
                <button
                  key={p.name}
                  onClick={() => setConfig(c => ({ ...c, url: p.url }))}
                  style={{ padding: '5px 10px', borderRadius: 4, fontSize: 10, cursor: 'pointer', border: '1px solid #ffffff14', background: '#0d1018', color: '#9ca3af', ...mono, textAlign: 'left', display: 'flex', justifyContent: 'space-between' }}
                >
                  <span>{p.name}</span>
                  <span style={{ color: '#4b5563' }}>{p.note}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Proxy badge */}
          <div style={{ marginBottom: 10, padding: '6px 10px', background: '#10b98110', borderRadius: 5, border: '1px solid #10b98133', fontSize: 10, ...mono, color: '#10b981' }}>
            ✓ Routed via backend proxy — CORS bypassed
          </div>

          {/* Send button */}
          <button
            onClick={testWebhook}
            disabled={!config.url || testStatus === 'sending'}
            style={{
              width: '100%', padding: 8, borderRadius: 5, fontSize: 11,
              cursor: (config.url && testStatus !== 'sending') ? 'pointer' : 'default',
              border: `1px solid ${btnColor}33`,
              background: btnBg,
              color: btnColor,
              ...mono,
              opacity: config.url ? 1 : 0.4,
              transition: 'all .2s',
            }}
          >
            {btnLabel}
          </button>

          {!config.enabled && config.url && (
            <div style={{ marginTop: 6, fontSize: 9, color: '#6b7280', ...mono, textAlign: 'center' }}>
              ℹ Test fires even when webhook is disabled
            </div>
          )}
        </div>

        {/* ── Delivery log ── */}
        <div style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em' }}>
              DELIVERY LOG (last 20)
            </div>
            {deliveryLog.length > 0 && (
              <button
                onClick={() => { setDeliveryLog([]); saveLog([]); }}
                style={{ fontSize: 9, color: '#4b5563', background: 'none', border: 'none', cursor: 'pointer', ...mono }}
              >
                clear
              </button>
            )}
          </div>

          {deliveryLog.length === 0 ? (
            <div style={{ fontSize: 11, color: '#374151', ...mono, textAlign: 'center', padding: 30 }}>
              No deliveries yet. Click "Send Test Payload" above.
            </div>
          ) : deliveryLog.map((entry, i) => {
            const ok      = entry.status?.includes('OK') || /^2\d\d/.test(entry.status);
            const pending = entry.status === '...';
            const col     = ok ? '#10b981' : pending ? '#f59e0b' : '#ff3b4e';
            return (
              <div key={entry.id ?? i} style={{ padding: '6px 0', borderBottom: '1px solid #ffffff08', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: col, flexShrink: 0, marginTop: 4 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 10, color: col, ...mono }}>{entry.status}</span>
                    <span style={{ fontSize: 9, color: '#4b5563', ...mono, flexShrink: 0 }}>
                      {entry.ts?.slice(11, 19)}
                    </span>
                  </div>
                  <div style={{ fontSize: 9, color: '#4b5563', ...mono, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.url}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payload reference */}
      <div style={panel}>
        <div style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em', marginBottom: 10 }}>
          PAYLOAD FORMAT (JSON)
        </div>
        <pre style={{ fontSize: 11, color: '#9ca3af', ...mono, background: '#0d1018', padding: 12, borderRadius: 6, overflow: 'auto', margin: 0 }}>
{`{
  "source":      "AEGIS SOC",
  "version":     "2.1",
  "timestamp":   "2025-05-05T10:01:00Z",
  "type":        "APT_ALERT",
  "severity":    "CRITICAL",
  "src_ip":      "10.0.1.49",
  "attack_type": "APT-C2-Communication",
  "confidence":  0.98,
  "incident_id": "APT-INC-0001"
}`}
        </pre>
      </div>
    </div>
  );
}
